import React, { useEffect, useMemo, useRef, useState } from 'react'
import { FiMail } from 'react-icons/fi'
import { useOrder } from '../../hooks/useAuth'
import {
  createOutboundMailDoc,
  infoToast,
  successToast,
} from '../../utils/firebase/firebase.utils'
import './custom-email-dialog.styles.scss'
import './reply-dialog.styles.scss'

const DEFAULT_FROM_EMAIL =
  process.env.REACT_APP_MAIL_FROM_EMAIL || 'cakebysandy01@gmail.com'

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const buildContacts = (inquiries, orders) => {
  const map = new Map()

  const upsert = (rawEmail, rawName, source) => {
    const email = String(rawEmail || '').trim()
    if (!email || !EMAIL_RE.test(email)) return
    const key = email.toLowerCase()
    const name = String(rawName || '').trim()
    const existing = map.get(key)
    if (!existing) {
      map.set(key, { email, name, sources: new Set([source]) })
      return
    }
    existing.sources.add(source)
    if (!existing.name && name) existing.name = name
  }

  ;(Array.isArray(inquiries) ? inquiries : []).forEach((item) => {
    if (item?.del === 'yes') return
    upsert(item.email, item.name, 'Inquiry')
  })

  ;(Array.isArray(orders) ? orders : []).forEach((item) => {
    if (item?.del === 'yes') return
    upsert(item.email, item.fullname || item.name, 'Order')
  })

  return Array.from(map.values())
    .map((c) => ({
      ...c,
      sources: Array.from(c.sources).sort(),
      label: c.name
        ? `${c.name} · ${c.email}`
        : c.email,
    }))
    .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }))
}

const CustomEmailDialog = () => {
  const { inquiries, orders, getInquiry, getOrders } = useOrder()
  const [open, setOpen] = useState(false)
  const [toEmail, setToEmail] = useState('')
  const [toName, setToName] = useState('')
  const [selectedKey, setSelectedKey] = useState('')
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [useDifferentEmail, setUseDifferentEmail] = useState(false)
  const [fromEmail, setFromEmail] = useState(DEFAULT_FROM_EMAIL)
  const [submitting, setSubmitting] = useState(false)
  const bodyRef = useRef(null)

  const contacts = useMemo(
    () => buildContacts(inquiries, orders),
    [inquiries, orders]
  )

  const openDialog = () => {
    setToEmail('')
    setToName('')
    setSelectedKey('')
    setSubject('')
    setBody('')
    setUseDifferentEmail(false)
    setFromEmail(DEFAULT_FROM_EMAIL)
    setOpen(true)

    if (typeof getInquiry === 'function') getInquiry()
    if (typeof getOrders === 'function') getOrders()
  }

  const close = () => {
    setOpen(false)
    setToEmail('')
    setToName('')
    setSelectedKey('')
    setSubject('')
    setBody('')
    setUseDifferentEmail(false)
    setFromEmail(DEFAULT_FROM_EMAIL)
  }

  useEffect(() => {
    if (!open) return undefined

    const onKeyDown = (event) => {
      if (event.key === 'Escape') close()
    }

    document.body.style.overflow = 'hidden'
    window.addEventListener('keydown', onKeyDown)

    const focusTimer = window.setTimeout(() => {
      bodyRef.current?.focus()
    }, 50)

    return () => {
      document.body.style.overflow = ''
      window.removeEventListener('keydown', onKeyDown)
      window.clearTimeout(focusTimer)
    }
  }, [open])

  const onSelectContact = (event) => {
    const key = event.target.value
    setSelectedKey(key)
    if (!key) {
      setToEmail('')
      setToName('')
      return
    }
    const contact = contacts.find((c) => c.email.toLowerCase() === key)
    if (!contact) return
    setToEmail(contact.email)
    setToName(contact.name || '')
  }

  const sendCustomEmail = async (event) => {
    event.preventDefault()

    const resolvedTo = toEmail.trim()
    if (!EMAIL_RE.test(resolvedTo)) {
      return infoToast('Enter or select a valid To email address.')
    }

    if (!subject.trim()) {
      return infoToast('Please add a subject.')
    }

    if (!body.trim()) {
      return infoToast('Please write a message before sending.')
    }

    const resolvedFrom = useDifferentEmail ? fromEmail.trim() : DEFAULT_FROM_EMAIL
    if (useDifferentEmail && !EMAIL_RE.test(resolvedFrom)) {
      return infoToast('Enter a valid From email address.')
    }

    setSubmitting(true)
    try {
      await createOutboundMailDoc({
        to: resolvedTo,
        to_name: toName.trim(),
        subject: subject.trim(),
        body: body.trim(),
        reply_from: resolvedFrom,
        created_at: new Date(),
        status: 'queued',
      })
      successToast('Email queued. It should send shortly.')
      close()
    } catch (error) {
      console.error('Custom email failed:', error)
      infoToast('Could not send email. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      <span className="admin-toolbar__mail-wrap">
        <button
          type="button"
          className="admin-toolbar__mail"
          onClick={openDialog}
          aria-label="Send custom email"
        >
          <FiMail size={15} aria-hidden="true" />
        </button>
        <span className="admin-toolbar__mail-tip" role="tooltip">
          Send custom email
        </span>
      </span>

      {open ? (
        <div className="reply-v2" role="dialog" aria-modal="true" aria-label="Send custom email">
          <div className="reply-v2__backdrop" onClick={close} aria-hidden="true" />
          <div
            className="reply-v2__panel custom-mail-v2__panel"
            onClick={(event) => event.stopPropagation()}
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="reply-v2__head">
              <div>
                <p className="reply-v2__eyebrow">Messaging</p>
                <h3>Send custom email</h3>
                <p>Pick a contact or type any email address</p>
              </div>
              <button type="button" className="reply-v2__close" onClick={close}>
                Close
              </button>
            </div>

            <form onSubmit={sendCustomEmail} className="reply-v2__form">
              <div className="reply-v2__field">
                <label htmlFor="custom-mail-contact">Contacts</label>
                <select
                  id="custom-mail-contact"
                  value={selectedKey}
                  onChange={onSelectContact}
                >
                  <option value="">Type email manually…</option>
                  {contacts.map((contact) => (
                    <option key={contact.email.toLowerCase()} value={contact.email.toLowerCase()}>
                      {contact.label}
                      {contact.sources.length ? ` (${contact.sources.join(', ')})` : ''}
                    </option>
                  ))}
                </select>
                {contacts.length === 0 ? (
                  <p className="custom-mail-v2__hint">
                    No inquiry or order emails yet. You can still type an address below.
                  </p>
                ) : null}
              </div>

              <div className="reply-v2__field">
                <label htmlFor="custom-mail-to">To</label>
                <input
                  id="custom-mail-to"
                  type="email"
                  value={toEmail}
                  onChange={(event) => {
                    setToEmail(event.target.value)
                    setSelectedKey('')
                    setToName('')
                  }}
                  placeholder="customer@example.com"
                  autoComplete="email"
                  required
                />
              </div>

              <div className="reply-v2__field">
                <span>From</span>
                <input
                  type={useDifferentEmail ? 'email' : 'text'}
                  readOnly={!useDifferentEmail}
                  tabIndex={useDifferentEmail ? 0 : -1}
                  className={useDifferentEmail ? undefined : 'reply-v2__from'}
                  value={useDifferentEmail ? fromEmail : DEFAULT_FROM_EMAIL}
                  onChange={(event) => setFromEmail(event.target.value)}
                  placeholder="you@example.com"
                  autoComplete="email"
                />
              </div>

              <label className="reply-v2__check">
                <input
                  type="checkbox"
                  checked={useDifferentEmail}
                  onChange={(event) => {
                    const checked = event.target.checked
                    setUseDifferentEmail(checked)
                    setFromEmail(checked ? '' : DEFAULT_FROM_EMAIL)
                  }}
                />
                <span>Use different email</span>
              </label>

              <div className="reply-v2__field">
                <label htmlFor="custom-mail-subject">Subject</label>
                <input
                  id="custom-mail-subject"
                  type="text"
                  value={subject}
                  onChange={(event) => setSubject(event.target.value)}
                  placeholder="Subject line"
                  required
                  autoComplete="off"
                />
              </div>

              <div className="reply-v2__field">
                <label htmlFor="custom-mail-body">Message</label>
                <textarea
                  id="custom-mail-body"
                  ref={bodyRef}
                  rows={6}
                  value={body}
                  onChange={(event) => setBody(event.target.value)}
                  placeholder="Write your message..."
                  required
                  autoComplete="off"
                />
              </div>

              <div className="reply-v2__actions">
                <button type="button" className="reply-v2__cancel" onClick={close}>
                  Cancel
                </button>
                <button type="submit" className="reply-v2__send" disabled={submitting}>
                  {submitting ? 'Sending…' : 'Send email'}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </>
  )
}

export default CustomEmailDialog
